import { getErrorMessage } from '@/api/errors'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { ENTITY_HISTORY_PAGE_SIZE, useEntityHistory } from '@/hooks'
import type { EntityHistoryEntityType } from '@/types'
import { X } from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'
import { EntityHistoryDetail } from './EntityHistoryDetail'
import { EntityHistoryList } from './EntityHistoryList'
import type { EntityHistoryFieldLabels } from './entityHistoryText'

interface EntityHistoryPanelProps {
  projectId: string
  entityType: EntityHistoryEntityType
  entityId: string
  entityLabel: string
  fieldLabels: EntityHistoryFieldLabels
  onClose: () => void
}

export function EntityHistoryPanel({
  projectId,
  entityType,
  entityId,
  entityLabel,
  fieldLabels,
  onClose,
}: EntityHistoryPanelProps) {
  const [maxResultCount, setMaxResultCount] = useState(ENTITY_HISTORY_PAGE_SIZE)
  const [selectedChangeGroupId, setSelectedChangeGroupId] = useState<string | null>(null)

  const headingId = useId()
  const detailRegionId = useId()
  const panelRef = useRef<HTMLDivElement>(null)

  // The control that opens this panel unmounts as it opens, so focus would otherwise fall back to
  // the dialog root and leave a keyboard user above the tab bar.
  useEffect(() => {
    panelRef.current?.focus()
  }, [])

  const { data, isLoading, error } = useEntityHistory(projectId, entityType, entityId, maxResultCount)

  const entries = data?.items ?? []
  const selectedEntry = entries.find(entry => entry.changeGroupId === selectedChangeGroupId)
  const hasMore = (data?.totalCount ?? 0) > entries.length

  return (
    <Card
      ref={panelRef}
      tabIndex={-1}
      role="region"
      aria-labelledby={headingId}
      className="p-8 mb-8 focus:outline-none"
      data-testid="entity-history-panel"
    >
      <div className="mb-6 flex items-center justify-between">
        <h2 id={headingId} className="text-2xl font-medium text-foreground">History</h2>
        <Button
          type="button"
          variant="ghost"
          size="iconLg"
          onClick={onClose}
          aria-label="Close history"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {isLoading && (
        <div className="space-y-2" role="status" aria-live="polite" aria-label="Loading history">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      )}

      {!isLoading && error && (
        <p role="alert" className="text-sm text-destructive">
          {getErrorMessage(error) ?? 'Failed to load history.'}
        </p>
      )}

      {!isLoading && !error && entries.length === 0 && (
        <p className="text-sm text-muted-foreground">No history recorded for this {entityLabel} yet.</p>
      )}

      {!isLoading && !error && entries.length > 0 && (
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="space-y-4">
            <EntityHistoryList
              entries={entries}
              entityLabel={entityLabel}
              fieldLabels={fieldLabels}
              selectedChangeGroupId={selectedChangeGroupId}
              detailRegionId={detailRegionId}
              onSelect={setSelectedChangeGroupId}
            />

            {hasMore && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setMaxResultCount(count => count + ENTITY_HISTORY_PAGE_SIZE)}
              >
                Show more
              </Button>
            )}
          </div>

          <div id={detailRegionId} aria-live="polite">
            {selectedEntry ? (
              <div className="rounded-md border p-4">
                <EntityHistoryDetail
                  entry={selectedEntry}
                  entityLabel={entityLabel}
                  fieldLabels={fieldLabels}
                />
              </div>
            ) : (
              <p className="text-sm text-muted-foreground" data-testid="entity-history-detail-empty">
                Select a change to see what it changed.
              </p>
            )}
          </div>
        </div>
      )}
    </Card>
  )
}
